import h from 'hyperscript';
import { t } from 'javascripts/utils/withTranslation';
import icon from 'components/_particles/icon/icon';
import Slider from '../../../javascripts/globals/slider';

export default class GallerySlider extends Slider {
  constructor($slider, mq, config) {
    super($slider, mq, config);

    this.$slider = $slider;
    this.showPagination = this.config.showPagination || false;
    this.showCount = this.config.showCount || false;

    this.initControls();
    super.initEvents();
  }

  initControls() {
    super.initControls();

    if (this.showPagination) {
      this.renderPagination();
    }

    if (this.showCount) {
      this.renderSlideCount();
    }

    this.positionControls();
    this.setContainerHeight();
  }

  updateControls() {
    this.updateSlideCount();
    this.updatePaginationPages();
  }

  handleOnLeftSwipe() {
    super.handleOnLeftSwipe();
    this.updateControls();
  }

  handleOnRightSwipe() {
    super.handleOnRightSwipe();
    this.updateControls();
  }

  handleResize() {
    super.handleResize();
    this.updateControls();
    this.updatePaginationNavigation();
    this.positionControls();
    this.setContainerHeight();
  }

  prevSlide() {
    super.prevSlide();
    this.updateControls();
    this.positionControls();
    this.setContainerHeight();
  }

  nextSlide() {
    super.nextSlide();
    this.updateControls();
    this.positionControls();
    this.setContainerHeight();
  }

  slideTo = (index) => {
    super.slideTo(index);
    if (this.activeIndex < this.maxIndex) {
      this.updateControls();
      this.positionControls();
      this.setContainerHeight();
    }
  };

  handleDotClick = (index) => {
    super.handleDotClick(index);
    this.updateControls();
  };

  updateArrows() {
    super.updateArrows();
    this.positionControls();
    this.setContainerHeight();
  }

  removePagination() {
    const getNode =
      this.$paginationWrapper ||
      this.$slider.querySelector('.slider__pagination');

    getNode?.parentNode.removeChild(getNode);
  }

  // add pagination for galleries
  renderPagination() {
    this.removePagination();
    if (!this.showPagination) {
      return;
    }

    const bindedPrevClick = this.prevSlide.bind(this);
    const bindedNextClick = this.nextSlide.bind(this);

    this.$prevPageButton = h(
      'button.button.button--secondary.button--reversed.pagination__icon-link',
      {
        type: 'button',
        onclick: () => bindedPrevClick(),
      },
      icon({
        icon: 'arrow-left',
        className: 'button__icon icon--arrow-left',
      }),
      h('span.button__text', t('Zurück'))
    );

    const $pages = this.getPaginationPages();

    this.$nextPageButton = h(
      'button.button.button--secondary.pagination__icon-link',
      {
        type: 'button',
        onclick: () => bindedNextClick(),
      },
      icon({
        icon: 'arrow-right',
        className: 'button__icon icon--arrow-right',
      }),
      h('span.button__text', t('Weiter'))
    );

    this.$paginationWrapper = h(
      '.pagination.slider__pagination',
      {
        'aria-label': t('Seitennummerierung'),
      },
      this.$prevPageButton,
      $pages,
      this.$nextPageButton
    );

    this.$slider.appendChild(this.$paginationWrapper);
    this.updatePaginationNavigation();
  }

  // calculate which page numbers are shown in the pagination and generate the DOM nodes
  getPaginationPages() {
    const bindedSlideTo = this.slideTo.bind(this);
    const isDotVisible = (i) =>
      i === 0 ||
      i === (this.$items?.length || 1) - 1 ||
      this.activeIndex === i ||
      this.activeIndex === i - 1; // eslint-disable-line

    const $pagination = h(
      'ul.pagination__list',
      this.$slidesIndexes.map((index) => {
        if (!isDotVisible(index - 1) && !isDotVisible(index)) {
          return undefined;
        }

        if (isDotVisible(index - 1) && !isDotVisible(index)) {
          return h('li.pagination__item', '…');
        }

        const ariaCurrent =
          index === this.activeIndex ? { 'aria-current': 'page' } : undefined;

        const $paginationButton = h(
          `button.link.link--inverted.pagination__link${
            index === this.activeIndex ? '.pagination__link--current' : ''
          }`,
          {
            type: 'button',
            onclick: () => bindedSlideTo(index),
            attrs: ariaCurrent,
          },
          h(
            'span.u-hidden-visually',
            index === this.$items.length - 1
              ? t('Zur letzten Seite')
              : t('Zur Seite')
          ),
          index + 1
        );

        return h(
          `li.pagination__item${
            index === this.activeIndex ? '.pagination__item--current' : ''
          }`,
          $paginationButton
        );
      })
    );
    return $pagination;
  }

  updatePaginationNavigation() {
    if (this.activeIndex === 0) {
      this.$prevPageButton?.setAttribute('disabled', true);
    } else {
      this.$prevPageButton?.removeAttribute('disabled');
    }

    if (this.activeIndex === this.numberOfSlides - this.itemsVisible) {
      this.$nextPageButton?.setAttribute('disabled', true);
    } else {
      this.$nextPageButton?.removeAttribute('disabled');
    }
  }

  updatePaginationPages() {
    if (!this.showPagination) {
      return;
    }
    const $pages = this.getPaginationPages();

    if ($pages) {
      const $wrapper = this.$slider.querySelector('.pagination__list');
      if ($wrapper && this.$paginationWrapper?.contains($wrapper)) {
        this.$paginationWrapper?.replaceChild($pages, $wrapper);
      }
    }

    this.updatePaginationNavigation();
  }

  removeSlideCount() {
    const getNode =
      this.$slideCountContainer ||
      this.$slider.querySelector('.slider__slide-count');

    getNode?.parentNode.removeChild(getNode);
  }

  // add initial count to slide controls
  renderSlideCount() {
    this.removeSlideCount();
    const $slideCount = h(
      'span.slider__slide-count-inner',
      `1/${this.numberOfSlides}`
    );
    this.$slideCountContainer = h('.slider__slide-count');
    this.$slideCountContainer.appendChild($slideCount);
    this.$scrollContainer.appendChild(this.$slideCountContainer);
  }

  updateSlideCount() {
    const $counter = this.$slider.querySelector('.slider__slide-count-inner');
    if ($counter) {
      $counter.innerText = `${this.activeIndex + 1}/${this.numberOfSlides}`;
    }
  }

  // calculate the control positions depending on images
  positionControls() {
    if (!this.showPagination) {
      return;
    }
    const $activeSlide = this.$items[this.activeIndex] || this.$items[0];

    if ($activeSlide) {
      const $activeSlideMedia = $activeSlide.querySelector('.figure__media');

      if ($activeSlideMedia) {
        const top = `${
          $activeSlideMedia.offsetTop + $activeSlideMedia.clientHeight / 2
        }px`;

        this.$controlNext?.setAttribute('style', `top: ${top}`);
        this.$controlPrev?.setAttribute('style', `top: ${top}`);

        if (this.$slideCountContainer) {
          this.$slideCountContainer.style.top = `${
            $activeSlideMedia.offsetTop + $activeSlideMedia.clientHeight + 8
          }px`;
        }
      }
    }
  }

  // calculate slide container height
  setContainerHeight() {
    if (!this.showPagination) {
      return;
    }

    const $activeSlide = this.$items[this.activeIndex] || this.$items[0];

    if ($activeSlide) {
      const $activeSlideFirstChild = $activeSlide.firstElementChild;

      if ($activeSlideFirstChild) {
        this.$scrollContainer.style.height = `${
          $activeSlideFirstChild.clientHeight + $activeSlideFirstChild.offsetTop
        }px`;
      }
    }
  }

  sizeVerticalImages() {
    super.sizeVerticalImages();
    this.updateControls();
  }
}
